import type { AddContractToUserResponse } from "@/models/dtos/add-contract-to-user-response";
import type { RemoveContractFromUserResponse } from "@/models/dtos/remove-contract-from-user-response";
import type { UserDTO } from "@/models/dtos/user-dto";
import type {
  CreatePhoneUserRequest,
  CreateUserRequest,
} from "@/models/requests/create-user-request";
import type { UpdateUserRequest } from "@/models/requests/update-user-request";
import { http } from "./http";
import { notificationsService } from "./notifications-service";
import { authService } from "@/services/auth-service";
import type { InactiveUserDTO } from "@/models/dtos/inactive-user-dto";
import type { EntraUserDTO } from "@/models/dtos/entra-user-dto.ts";

class UsersService {
  async getEntraUsers(
    query?: Record<string, unknown>,
  ): Promise<EntraUserDTO[]> {
    const authHeader = await authService.getAuthHeader();
    return http
      .url("/entra")
      .auth(authHeader)
      .query(query ?? {})
      .get()
      .fetchError((err) => notificationsService.error(err))
      .json<EntraUserDTO[]>();
  }
  async getUsers(query?: Record<string, unknown>): Promise<UserDTO[]> {
    const authHeader = await authService.getAuthHeader();
    return http
      .url("/users")
      .auth(authHeader)
      .query(query ?? {})
      .get()
      .fetchError((err) => notificationsService.error(err))
      .json<UserDTO[]>();
  }

  async getUser(userId: number | string): Promise<UserDTO> {
    const authHeader = await authService.getAuthHeader();
    return http
      .url(`/users/${userId}`)
      .auth(authHeader)
      .get()
      .fetchError((err) => notificationsService.error(err))
      .json<UserDTO>();
  }

  async getInactiveUsers(customerNumber: string): Promise<InactiveUserDTO[]> {
    const authHeader = await authService.getAuthHeader();
    return http
      .url("/users/all")
      .auth(authHeader)
      .query({ customerNumber })
      .get()
      .fetchError((err) => notificationsService.error(err))
      .json<InactiveUserDTO[]>();
  }

  async createUser(request: CreateUserRequest): Promise<UserDTO> {
    const authHeader = await authService.getAuthHeader();
    return http
      .url("/users")
      .auth(authHeader)
      .post(request)
      .fetchError((err) => notificationsService.error(err))
      .json<UserDTO>();
  }

  async createPhoneUser(request: CreatePhoneUserRequest): Promise<UserDTO> {
    const authHeader = await authService.getAuthHeader();
    return http
      .url("/phoneusers")
      .auth(authHeader)
      .post(request)
      .fetchError((err) => notificationsService.error(err))
      .json<UserDTO>();
  }

  async updateUser(
    userId: number,
    request: UpdateUserRequest,
  ): Promise<UserDTO> {
    const authHeader = await authService.getAuthHeader();
    return http
      .url(`/users/${userId}`)
      .auth(authHeader)
      .put(request)
      .fetchError((err) => notificationsService.error(err))
      .json<UserDTO>();
  }

  async deleteUser(userId: number): Promise<void> {
    const authHeader = await authService.getAuthHeader();
    await http
      .url(`/users/${userId}`)
      .auth(authHeader)
      .delete()
      .fetchError((err) => notificationsService.error(err))
      .res();
  }

  async deleteUserFromCustomer(
    userId: number,
    customerNumber: string,
  ): Promise<void> {
    const authHeader = await authService.getAuthHeader();
    await http
      .url(`/users/${userId}/customer/${customerNumber}`)
      .auth(authHeader)
      .delete()
      .fetchError((err) => notificationsService.error(err))
      .res();
  }

  async addContractToUser(
    userId: string | number,
    contractId: number,
  ): Promise<AddContractToUserResponse> {
    const authHeader = await authService.getAuthHeader();
    return http
      .url(`/users/${userId}/contract/${contractId}`)
      .auth(authHeader)
      .post()
      .fetchError((err) => notificationsService.error(err))
      .json<AddContractToUserResponse>();
  }

  async removeContractFromUser(
    userId: string | number,
    contractId: number,
  ): Promise<RemoveContractFromUserResponse> {
    const authHeader = await authService.getAuthHeader();
    return http
      .url(`/users/${userId}/contract/${contractId}`)
      .auth(authHeader)
      .delete()
      .fetchError((err) => notificationsService.error(err))
      .json<RemoveContractFromUserResponse>();
  }
}

export const usersService = new UsersService();
